import React, { createContext, useState, useContext, useEffect } from 'react';
import { cartAPI } from '../services/cartAPI';
import { PublicProductAPI } from '../services/PublicProductAPI';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
    const [cartItems, setCartItems] = useState([]);
    const [loading, setLoading] = useState(false);

    const [selectedShipping, setSelectedShipping] = useState(null);

    // CartContext.js

    const fetchCart = async () => {
        // 1. Verifica se o token existe no localStorage antes de chamar a API
        const token = localStorage.getItem('token');

        if (!token) {
            // Se não tem token, nem tenta bater na API. 
            // Apenas garante que o carrinho comece vazio.
            setCartItems([]);
            return;
        }

        try {
            const response = await cartAPI.list();
            setCartItems(response || []);
        } catch (error) {
            // Se o token existir mas for inválido (erro 401), limpamos ele
            if (error.response?.status === 401 || error.response?.status === 422) {
                localStorage.removeItem('token');
                setCartItems([]);
            }
        }
    };

    useEffect(() => {
        fetchCart();
    }, []);

    useEffect(() => {
        fetchCart();
    }, []);

    useEffect(() => {
        setSelectedShipping(null);
    }, [cartItems.length]);

    const addToCart = async (productId, option) => {
        setLoading(true);
        try {
            // Front-end stock validation (best-effort). Server-side must still validate.
            try {
                const prod = await PublicProductAPI.get_product_by_id(productId);
                if (prod && prod.stock_quantity === 0) {
                    alert('Produto esgotado.');
                    setLoading(false);
                    return;
                }
            } catch (err) {
                // ignore and try add; server validation will enforce correctness
            }

            await cartAPI.add({ product_id: productId, option });
            await fetchCart();
        } catch (error) {
            alert("Erro ao adicionar ao carrinho");
        } finally {
            setLoading(false);
        }
    };

    const removeFromCart = async (itemId) => {
        try {
            await cartAPI.remove(itemId);
            setCartItems(prev => prev.filter(item => item.id !== itemId));
        } catch (error) {
            console.error("Erro ao remover:", error);
        }
    };

    const updateQuantity = async (itemId, newQuantity) => {
        if (newQuantity < 1) return;

        try {
            // Atualiza a API
            await cartAPI.update(itemId, { quantity: newQuantity });

            // Atualiza o estado local para refletir no subtotal e total imediatamente
            setCartItems(prev => prev.map(item =>
                item.id === itemId ? { ...item, quantity: newQuantity } : item
            ));
        } catch (error) {
            console.error("Erro ao atualizar quantidade:", error);
            // Opcional: recarregar o carrinho em caso de erro para sincronizar
            fetchCart();
        }
    };

    const cartCount = (cartItems || []).reduce((acc, item) => acc + (item.quantity || 0), 0);

    const subtotal = (cartItems || []).reduce((acc, item) => {
        const price = item.product_details?.price || 0;
        return acc + (price * (item.quantity || 0));
    }, 0);

    // No CartContext.js, altere a linha do totalOrder:
    const totalOrder = subtotal + (selectedShipping?.preco || selectedShipping?.price || 0);

    return (
        <CartContext.Provider value={{
            cartItems,
            addToCart,
            removeFromCart,
            updateQuantity,
            cartCount,
            loading,
            selectedShipping,
            setShipping: setSelectedShipping,
            subtotal,
            totalOrder
        }}>
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => useContext(CartContext);